'use client'
import { Fragment, useState, useEffect, useRef, useMemo } from "react";
import Link from "next/link";
import { Element } from "react-scroll";
import useMount from "react-use/lib/useMount";
import styles from './NewProperties.module.css';
import buttons from "components/_styled/buttons.module.css";
import { checkLoggedIn } from "context/context";
import NavBar from "./NavBar";
import ScrollToTop from "components/ScrollToTop";
import LazyImage from "components/LazyImage";
import interpretThumbnail from "lib/interpretThumbnail";
import logPageView from "lib/ga/pageView";
import {  ResultThumbnail } from "lib/types";

// Add anything places you want to merge here.
// eg:
// greater-london: 'london'    meakes all greater-london places merge into london
const placesMergeMap = {
  "greater-london": "london",
  "city-of-london": "london",
};

type image = {
  format: object
}


const getThumbnail = (rawResult: {images: image[]}, searchType: string | undefined, categoryCode: string) => {
  let images = rawResult.images || [];
  const thumb: ResultThumbnail = {
    origin: null,
    default:
      searchType === "SCHEME" && categoryCode === "SC"
        ? "fallback-images/scheme-sc-fullback.jpeg"
        : "fallback-images/" + categoryCode?.toLowerCase() + "-fullback.jpeg",
    sizes: [200, 400, 600, 800],
  };

  // get the real one
  if (images.length > 0) {
    thumb.origin = "this";
    Object.keys(images[0].format)
      .map((k) => ({ ...images[0].format[k], file_type: k.endsWith('wp') ? 'webp' : 'jpg' }))
      .forEach((med) => {
        if (thumb.sizes.includes(med.width)) {
          if (med.file_type in thumb) {
            thumb[med.file_type][med.width] = med.uri;
          } else {
            thumb[med.file_type] = {
              [med.width]: med.uri,
            };
          }
        }
      });

    if ("jpg" in thumb) {
      thumb.default = thumb.jpg?.[200];
    } else if ("png" in thumb) { thumb.default = thumb.png?.[200] } else {
      thumb.origin = null;
    }
  }
  return thumb;
};

type agent = {
  paying_client: boolean,
  company_id: number,
  company_name: string
}

type PropertyRowProps = {
  full_name: string, rent_pa: number, size_ft: string, url_fragment: string, id: number, category_code: string, type: string | undefined, line1:string , town_name: string, postcode: string, agents: agent[], premium: boolean, loggedIn: boolean
}

const PropertyRow = (props) => {
  const { full_name, rent_pa, size_ft, url_fragment, id, category_code, type, line1, town_name, postcode, agents, premium, loggedIn }:PropertyRowProps  = props;

  const propertyLinkRef = useRef<HTMLAnchorElement>(null);
  const address = [line1, town_name, postcode].filter(Boolean).join(", ")
  const thumbnailProps = useMemo(() => {
    return interpretThumbnail(getThumbnail(props, type, category_code), full_name ? full_name : address, type, category_code);
  }, [full_name, type, category_code, address]);
  const filteredAgents = agents.filter((a) => premium || a.paying_client);
  return (
    <div className={styles.PropertyWrapper}>
      <li className={styles.Property}
        onClick={(e: any) => {
          if (e.target.tagName !== "BUTTON" && e.target.tagName !== "A") {
            propertyLinkRef?.current?.click();
          }
        }}
        title={full_name ? full_name : "--"}
      >
        <div className={styles.PropertyImage}>
          <LazyImage {...thumbnailProps} />
        </div>
        <div>
          <h5>{full_name}</h5>
          {address && <p>{address}</p>}
        </div>
        <div className={styles.Content}>
          <div className={styles.MobileOnly}>Marketed by:</div>
          <section>
            {filteredAgents.length > 0 &&
              filteredAgents.map((agent, index) => {
                if(loggedIn) {
                  return (
                    <Link key={agent.company_id} href={`/overview/${agent.company_id}`} passHref prefetch={false} legacyBehavior>
                      <a title={"Explore " + agent.company_name + " on Completely Retail"}>
                        <span>{agent.company_name}</span>
                        {filteredAgents.length > 1 && filteredAgents.length !== (index + 1) && (<span>,</span>)}
                      </a>
                    </Link>
                  )
                } else {
                  return (
                  <p key={agent.company_id}>{agent.company_name}{filteredAgents.length > 1 && filteredAgents.length !== (index + 1) && (<span>,</span>)}</p>
                  )
                }
              })
            }
          </section>
        </div>
        <div className={styles.Content}>
          <div className={styles.MobileOnly}>Rent:</div>
          <section>{rent_pa ? `£${rent_pa.toLocaleString()}` : "PoA"}</section>
        </div>
        <div className={styles.Content}>
          <div className={styles.MobileOnly}>Size:</div>
          <section>{size_ft ? `${size_ft.toLocaleString()} ft` : "-"}</section>
        </div>
        <div>
          <Link href={`/property/${url_fragment ? url_fragment : id}`} passHref prefetch={false} legacyBehavior>
            <a className={buttons.primaryButton} ref={propertyLinkRef}>
              View
            </a>
          </Link>
        </div>
      </li>
    </div>
  );
};

type TownAndPropertiesProps = {
  data: TownAndPropertiesPropsData,
  name: string,
  noBorder: boolean,
  loggedIn: boolean
}

type TownAndPropertiesPropsData = {
  uri: string,
  total: number,
  // property: {
  //   _source: object
  // },
  properties: Array<Property>
}
const TownAndProperties = ({ data, name, noBorder, loggedIn }: TownAndPropertiesProps) => {
  const { uri, total, properties } = data;
  // console.log(properties);
  return (
    <section className={styles.Town} key={name}>
      <div className={`${styles.Overview} ${noBorder ? styles.noBorder : ''}`}>
        <h4>{name}</h4>
      </div>
      {<section className={styles.Properties}>
        <ul>
          {properties.map((prop) => (
            <PropertyRow
              key={prop._id}
              {...prop._source}
              loggedIn={loggedIn}
            />
          ))}
          <li className={styles.ViewAll}>
            <Link href={`/shops-to-rent/${uri}`} passHref prefetch={false} legacyBehavior>
              <a className={buttons.primaryButton}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >{`View all properties in ${name}`}</a>
            </Link>
          </li>
        </ul>
      </section>}
    </section>
  );
};

type Props = {
  properties: Properties
}

type Property = {
  _source: {
    town_name: string;
    town_uri: string;
  };
  // Add any other properties of the 'property' object if necessary
};

type Properties = {
  [key: string]: Property;
};

type TownData = {
  [letter: string]: {
    [townName: string]: {
      // property: Property;
      properties: Array<Property>;
      total: number;
      uri: string;
    };
  };
};

const NewProperties = ({ properties }: Props) => {
  const [menuVisible, setMenuVisible] = useState(false);
  const loggedIn = checkLoggedIn();
  const checkForMenuClose = (e) => {
    if (
      menuVisible
      &&
      e.target.id !== 'cr-NEW-menuButton'
    )
      setMenuVisible(false);
  };

  useMount(() => {
    logPageView({ url: window.location.pathname + window.location.search });
  });

  useEffect(() => {
    window.addEventListener("click", checkForMenuClose);
    return () => {
      window.removeEventListener("click", checkForMenuClose);
    };
  });

  const sortData = obj => {
    // get the keys of the object
    var keys = Object.keys(obj);

    // sort the keys
    keys.sort();

    // use reduce to rebuild the object
    return keys.reduce((sortedObj, key) => {
      sortedObj[key] = obj[key];
      return sortedObj;
    }, {});
  };

  const townData: TownData = {};

  Object.keys(properties).forEach(key => {
    const firstLetter = properties[key]._source.town_name.charAt(0)
    if (townData[firstLetter] === undefined) {
      townData[firstLetter] = {}
      townData[firstLetter][properties[key]._source.town_name] = { properties: [], total: 1, uri: properties[key]._source.town_uri };
    } else if (townData[firstLetter][properties[key]._source.town_name] === undefined) {
      townData[firstLetter][properties[key]._source.town_name] = { properties: [], total: 1, uri: properties[key]._source.town_uri };
      townData[firstLetter][properties[key]._source.town_name].uri = properties[key]._source.town_uri;
    } else {
      townData[firstLetter][properties[key]._source.town_name].total = townData[firstLetter][properties[key]._source.town_name].total + 1;
    }

    if (Array.isArray(townData[firstLetter][properties[key]._source.town_name].properties)) {
      townData[firstLetter][properties[key]._source.town_name].properties = [...townData[firstLetter][properties[key]._source.town_name].properties, properties[key]]
    } else {
      townData[firstLetter][properties[key]._source.town_name].properties = Array(properties[key])
    }
  })

  return (
    <>
      <button className={styles.MenuButton} id="cr-NEW-menuButton" onClick={() => setMenuVisible(!menuVisible)}>
        Jump to letter
      </button>
      <section className={styles.Header}>
        <div className={styles.wrapper}>
          <h1>Latest Properties on Completely Retail</h1>
        </div>
      </section>
      <section className={styles.InTouch}>
        <div className={styles.wrapper}>
          <h2>{`Below you'll find a selection of some of our most recently added properties from around the UK`}</h2>
          <p>
            New properties are added every day from all across the UK, whether you're looking for prime locations for your first
            shop, or that perfect retail unit for your latest store. Find the latest retail properties on the market here.
          </p>
        </div>
      </section>

      <NavBar
        menuVisible={menuVisible}
        closeMenu={() => {
          setMenuVisible(false);
        }}
        alphabetisedLocations={townData || {}}
      />

      <div className={styles.TableHeader}>
        <section className={styles.Columns}>
          <div></div>
          <div>Name</div>
          <div>Marketed By</div>
          <div>Rent (£ pa)</div>
          <div>Area (sq ft)</div>
          <div></div>
        </section>
      </div>

      <section className={styles.Towns}>
        {townData ? (
          Object.keys(townData).map((letter) => (
            <Fragment key={letter}>
              <div className={styles.Letter}>
                <Element name={`startsWith_${letter.toLocaleLowerCase()}`} />
                <h3>{letter.toUpperCase()}</h3>
              </div>
              {Object.keys(townData[letter]).map((location, i) => (
                <TownAndProperties loggedIn={loggedIn} key={location} data={townData[letter][location]} name={location} noBorder={i === 0}/>
              ))}
            </Fragment>
          ))
        ) : (
          <h3>No new properties</h3>
        )}
      </section>

      <ScrollToTop />
    </>
  );
};

export default NewProperties;
